import { clearLocalStoragePreserveEssentials, getEnvironment } from "./util";

const origin = window.location.origin;
const environment = getEnvironment()?.toLowerCase();

let authUrl = process.env.REACT_APP_AUTH_URL_DEV;
let clientId = process.env.REACT_APP_CLIENT_ID_DEV;

if (environment === "prod") {
  const isSandbox = origin?.includes("sandbox");

  authUrl = isSandbox ? process.env.REACT_APP_AUTH_URL_SANDBOX : process.env.REACT_APP_AUTH_URL;

  clientId = isSandbox ? process.env.REACT_APP_CLIENT_ID_SANDBOX : process.env.REACT_APP_CLIENT_ID;
}

const userInfo = JSON.parse(localStorage.getItem("userInfo"));

export const isIdentityServerEnabled = () => {
  return process.env.REACT_APP_USE_IDENTITY_SERVER === "true";
};

export const redirectToIdentityServerLogin = async (webPortal, externalProvider = null) => {
  const pkceCodeVerifier = generatePkceCodeVerifier();
  const base64Url = await pkceChallengeFromVerifier(pkceCodeVerifier);

  localStorage.setItem("pkce_code_verifier", pkceCodeVerifier);

  const queryParam = new URLSearchParams({
    client_id: clientId,
    redirect_origin: origin,
    redirect_path: "signin-oidc",
    code_challenge: base64Url,
    environment: getEnvironment(),
    web_portal: webPortal || `${origin}/`,
  });

  if (externalProvider) {
    queryParam.append("external_provider", externalProvider);
  }

  const url = `${authUrl}/auth/login?` + queryParam.toString();

  window.location.href = url;
};

export function generatePkceCodeVerifier(length = 64) {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, (dec) => ("0" + dec.toString(16)).slice(-2)).join("");
}

export const pkceChallengeFromVerifier = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);

  return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const getAuthorizationCode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("code");
};

export const exchangeAuthCodeForToken = async (code) => {
  const pkceCodeVerifier = localStorage.getItem("pkce_code_verifier");

  const body = {
    client_id: clientId,
    redirect_origin: origin,
    redirect_path: "signin-oidc",
    code: code,
    code_verifier: pkceCodeVerifier,
  };

  const response = await fetch(authUrl + "/auth/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Token exchange failed: " + response.statusText);
  }

  const responseData = await response.json();
  return responseData;
};

export const refreshAccessToken = async () => {
  const body = {
    client_id: clientId,
    redirect_origin: origin,
    session_id: userInfo?.sessionId,
  };

  const url = `${authUrl}/auth/refreshtoken`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Token exchange failed: " + response.statusText);
  }

  const responseData = await response.json();
  return responseData;
};

export async function fetchUserInfo(accessToken) {
  const response = await fetch(authUrl + "/connect/userinfo", {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user info: " + response.statusText);
  }

  const responseData = await response.json();

  if (!responseData.userDetail) {
    throw new Error("Failed to fetch user info: user detail not found");
  }

  localStorage.removeItem("pkce_code_verifier"); // Immediately remove for security reason

  return responseData;
}

export const redirectToAuthChangePassword = async () => {
  const url = `${authUrl}/AccountManager/ChangePassword`;

  window.location.href = url;
};

export const setAuthRole = async (targetRole) => {
  const params = {
    target_role: targetRole,
  };

  const url = `${authUrl}/AccountManager/SwitchRole?` + new URLSearchParams(params).toString();

  window.location.href = url;
};

export const redirectToIdentityServerLogout = async () => {
  clearLocalStoragePreserveEssentials();

  const url =
    `${authUrl}/auth/logout?` +
    new URLSearchParams({
      client_id: clientId,
      redirect_origin: origin,
    }).toString();

  window.location.href = url;
};

export const redirectToHenngeDirectLogin = async () => {
  const hostname = window.location.hostname;
  const providerScheme = `HENNGE-${hostname}`;

  await redirectToIdentityServerLogin(null, providerScheme);
};
